import { useState, useEffect } from 'react'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { Typography, Button, TextField, Grid, Select, MenuItem, IconButton, Box } from '@mui/material'
import redCross from 'asserts/images/redCross.svg'
import greenTick from 'asserts/images/greenTick.svg'
import tickfilled from 'asserts/images/tickfilled.svg'
import crossfilled from 'asserts/images/crossfilled.svg'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import Confirmation from 'Components/ReusableComponents.js/ConfirmationPopup/Confirmation'
import { postCreateNewTab } from 'store/action/createNewTab'
import { useDispatch, useSelector } from 'react-redux'
import { getLeaderboardTabs } from 'store/action/leaderboardTabs'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

const AddNewForm = ({ setPromptOnTabChange, setSelectedTab, editData, setEditData }) => {
  const dispatch = useDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [showSuccessPopup, setShowSuccessPopup] = useState(false)
  const [showErrorPopup, setShowErrorPopup] = useState(false)
  const leaderboardTabs = useSelector((state) => state?.leaderboardtabs?.data)
  const totalPositions = (leaderboardTabs?.count || 0) + 1

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      tabName: '',
      position: '',
      infoIconEnabled: null,
      infoIconText: '',
      rankEnabled: null,
      csvPath: '',
      keys: '',
      labels: '',
      weights: '',
    },
    mode: 'onChange',
  })
  useEffect(() => {
    if (isDirty) {
      setPromptOnTabChange(true)
    }
  }, [isDirty, setPromptOnTabChange])

  useEffect(() => {
    dispatch(getLeaderboardTabs())
  }, [dispatch])

  const infoIconEnabled = watch('infoIconEnabled')
  const rankEnabled = watch('rankEnabled')

  const onSubmit = async (data) => {
    if (rankEnabled == null || infoIconEnabled == null) {
      return
    }

    try {
      setIsSubmitting(true)
      const payload = {
        tabName: data.tabName,
        position: Number(data.position),
        infoIconEnabled: data.infoIconEnabled,
        infoIconText: data.infoIconEnabled ? data.infoIconText : '',
        rankEnabled: data.rankEnabled,
        csvPath: data.csvPath,
        keys: data.keys,
        labels: data.labels,
        weights: data.weights,
        ...(editData?.id && { id: editData.id }),
      }

      const result = await dispatch(postCreateNewTab(payload))

      if (result?.message) {
        setShowSuccessPopup(true)
        setPromptOnTabChange(false)
      }
    } catch (error) {
      console.error('Error submitting form:', error)
      setShowErrorPopup(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleConfirmCancel = () => {
    setShowConfirm(false)
    reset() // Clears the form
    setPromptOnTabChange(false)
    setEditData(null)
  }

  useEffect(() => {
    if (editData) {
      reset({
        tabName: editData.tabName || '',
        position: editData.position?.toString() || '',
        infoIconEnabled: editData.infoIconEnabled ?? null,
        infoIconText: editData.infoIconText || '',
        rankEnabled: editData.rankEnabled ?? null,
        csvPath: editData.csvPath || '',
        keys: editData.keys || '',
        labels: editData.labels || '',
        weights: editData.weights || '',
      })
    } else {
      reset({
        tabName: '',
        position: '',
        infoIconEnabled: null,
        infoIconText: '',
        rankEnabled: null,
        csvPath: '',
        keys: '',
        labels: '',
        weights: '',
      })
    }
  }, [editData, reset])

  return (
    <>
      <form>
        <Grid container spacing={2} justifyContent="center" alignItems="flex-start" mt={2}>
          <Grid item xs={12} md={4}>
            <Grid container spacing={2}>
              <Grid item xs={12} sx={{ p: 3 }}>
                <Typography className="createnew-event-textfield-label mb-2">
                  Name<span style={{ color: errors.tabName ? 'red' : 'inherit' }}>*</span>
                </Typography>
                <TextField
                  {...register('tabName', {
                    required: 'Please Enter Name',
                    validate: (value) => {
                      let trimmedValue = value.replace(/^\s+/, '')
                      if (trimmedValue.length > 35) return 'Maximum 35 characters allowed'
                      return trimmedValue.length > 0 || 'Please Enter Name'
                    },
                  })}
                  autoComplete="off"
                  error={!!errors.tabName}
                  helperText={
                    errors.tabName ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />
                        {errors.tabName.message}
                      </span>
                    ) : (
                      ' '
                    )
                  }
                  inputProps={{ maxLength: 35 }}
                  fullWidth
                  sx={{
                    '& input': {
                      fontFamily: 'HK Grotesk',
                      fontWeight: 'bold',
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sx={{ p: 3 }}>
                <Typography className="createnew-event-textfield-label mb-2">
                  Position<span style={{ color: errors.position ? 'red' : 'inherit' }}>*</span>
                </Typography>
                <Controller
                  name="position"
                  control={control}
                  defaultValue=""
                  rules={{ required: 'This field is required' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      displayEmpty
                      error={!!errors.position}
                      sx={{
                        '& .MuiSelect-select': {
                          fontFamily: 'HK Grotesk',
                          fontWeight: 'bold',
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        Select
                      </MenuItem>
                      {[...Array(totalPositions).keys()].map((pos) => (
                        <MenuItem key={pos + 1} value={pos + 1}>
                          {pos + 1}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Grid>

              <Controller
                name="infoIconEnabled"
                control={control}
                rules={{
                  validate: (value) => value === true || value === false || '',
                }}
                render={({ field }) => (
                  <Grid item xs={12} sx={{ p: 3, display: 'flex', alignItems: 'center' }}>
                    <Typography className="createnew-event-textfield-label mr-3">
                      i Icon<span style={{ color: errors.infoIconEnabled ? 'red' : 'inherit' }}>*</span> :
                    </Typography>

                    <IconButton onClick={() => field.onChange(true)} sx={{ p: 1 }}>
                      <img src={field.value === true ? tickfilled : greenTick} alt="Enable" />
                    </IconButton>

                    <IconButton onClick={() => field.onChange(false)} sx={{ p: 1 }}>
                      <img src={field.value === false ? crossfilled : redCross} alt="Disable" />
                    </IconButton>

                    {errors.infoIconEnabled && (
                      <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                        {errors.infoIconEnabled.message}
                      </Typography>
                    )}
                  </Grid>
                )}
              />

              {infoIconEnabled && (
                <Grid item xs={12} sx={{ p: 3 }}>
                  <Typography className="createnew-event-textfield-label mb-2">
                    Info Icon Text<span style={{ color: errors.infoIconText ? 'red' : 'inherit' }}>*</span>
                  </Typography>
                  <TextField
                    placeholder="Enter info icon text"
                    variant="outlined"
                    fullWidth
                    sx={{
                      '& input': {
                        fontFamily: 'HK Grotesk',
                        fontWeight: 'bold',
                      },
                    }}
                    {...register('infoIconText', {
                      validate: (value) => {
                        if (infoIconEnabled && value.trim().length === 0) {
                          return 'Please Enter i Icon Text'
                        }
                        return true
                      },
                    })}
                    error={!!errors.infoIconText}
                    helperText={
                      errors.infoIconText ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />
                          {errors.infoIconText.message}
                        </span>
                      ) : (
                        ' '
                      )
                    }
                  />
                </Grid>
              )}
            </Grid>
          </Grid>

          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <Grid item xs={12} sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
                <Typography className="createnew-event-textfield-label mb-2" style={{ fontSize: '1.2rem' }}>
                  Card Details:
                </Typography>
                <Controller
                  name="rankEnabled"
                  control={control}
                  rules={{
                    validate: (value) => value === true || value === false || '',
                  }}
                  render={({ field }) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Typography className="createnew-event-textfield-label mr-3">
                        Rank<span style={{ color: errors.rankEnabled ? 'red' : 'inherit' }}>*</span> :
                      </Typography>

                      <IconButton onClick={() => field.onChange(true)} sx={{ p: 1 }}>
                        <img src={field.value === true ? tickfilled : greenTick} alt="Enable" />
                      </IconButton>

                      <IconButton onClick={() => field.onChange(false)} sx={{ p: 1 }}>
                        <img src={field.value === false ? crossfilled : redCross} alt="Disable" />
                      </IconButton>

                      {errors.rankEnabled && (
                        <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                          {errors.rankEnabled.message}
                        </Typography>
                      )}
                    </Box>
                  )}
                />
              </Grid>
              <Grid item xs={12} sx={{ p: 3 }}>
                <Typography className="createnew-event-textfield-label mb-3">
                  File Path
                  <span
                    style={{
                      color: errors.csvPath || errors.keys || errors.labels || errors.weights ? 'red' : 'inherit',
                    }}
                  >
                    *
                  </span>{' '}
                  :
                </Typography>
                <Grid container spacing={1} alignItems="center" mt={2}>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      sx={{
                        '& input': {
                          fontFamily: 'HK Grotesk',
                          fontWeight: 'bold',
                        },
                      }}
                      placeholder="Query"
                      {...register('csvPath', {
                        validate: (value) => value.trim().length > 0 || 'File Path is required',
                      })}
                      error={!!errors.csvPath}
                    />
                  </Grid>

                  <Grid item>
                    <ChevronRightIcon sx={{ color: '#356f92', fontSize: 30 }} />
                  </Grid>

                  <Grid item xs={2}>
                    <TextField
                      fullWidth
                      sx={{
                        '& input': {
                          fontFamily: 'HK Grotesk',
                          fontWeight: 'bold',
                        },
                      }}
                      placeholder="Key"
                      {...register('keys', {
                        validate: (value) => value.trim().length > 0 || 'Key is required',
                      })}
                      error={!!errors.keys}
                    />
                  </Grid>

                  <Grid item>
                    <ChevronRightIcon sx={{ color: '#356f92', fontSize: 30 }} />
                  </Grid>

                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      sx={{
                        '& input': {
                          fontFamily: 'HK Grotesk',
                          fontWeight: 'bold',
                        },
                      }}
                      placeholder="Key of UI"
                      {...register('labels', {
                        validate: (value) => value.trim().length > 0 || 'UI Key is required',
                      })}
                      error={!!errors.labels}
                    />
                  </Grid>

                  <Grid item>
                    <ChevronRightIcon sx={{ color: '#356f92', fontSize: 30 }} />
                  </Grid>

                  <Grid item xs={1.3}>
                    <TextField
                      fullWidth
                      sx={{
                        '& input': {
                          fontFamily: 'HK Grotesk',
                          fontWeight: 'bold',
                        },
                      }}
                      placeholder="Weight"
                      {...register('weights', {
                        validate: (value) => value.trim().length > 0 || 'Weight is required',
                      })}
                      error={!!errors.weights}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Box className="d-flex mt-4 justify-content-center mb-3">
          <Button
            variant="contained"
            sx={{ p: 1, mr: 4, backgroundColor: '#ef7335', fontWeight: 'bold' }}
            className="set-btn button-tr-2-13-disa"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
          >
            Submit
          </Button>
          <Button
            variant="outlined"
            sx={{ p: 1, color: '#0d3d70 !important', fontWeight: 'bold', borderColor: '#0d3d70 !important' }}
            className="set-btn button-tr-2-1"
            type="button"
            onClick={() => {
              if (isDirty) {
                setShowConfirm(true)
              }
            }}
          >
            Cancel
          </Button>
        </Box>
      </form>
      {showConfirm && (
        <Confirmation
          text="All entered data will be lost. Please confirm"
          affirm="Confirm"
          cancel="Cancel"
          affirmFunction={handleConfirmCancel}
          cancelFunction={() => setShowConfirm(false)}
        />
      )}
      {showSuccessPopup && (
        <Confirmation
          text="The details have been submitted successfully"
          // affirm="Done"
          cancel="Done"
          // affirmFunction={() => {
          //   setShowSuccessPopup(false)
          //   setSelectedTab(0) // ✅ Tab change only after user clicks "Done"
          // }}
          cancelFunction={() => {
            setShowSuccessPopup(false)
            setSelectedTab(0)
            setEditData(null)
          }} // no-op
        />
      )}
      {showErrorPopup && (
        <Confirmation
          text="An error has occurred while submitting details."
          // affirm="Retry"
          cancel="Retry"
          affirmFunction={() => {
            setShowErrorPopup(false)
            handleSubmit(onSubmit)()
          }}
          cancelFunction={() => setShowErrorPopup(false)}
        />
      )}
    </>
  )
}

export default AddNewForm
